import { Currency } from "@/types/sos-types";
import { AsyncStepGraph } from "./async-step-graph";
import { Job } from "./job-by-name";
import { Adjustment, getAdjustments } from "./tracking-data/adjustments";
import { BomComponent, getBomByComponent, getSalesOrderBomComponents } from "./tracking-data/bom-by-component";
import { BomNode } from "./tracking-data/bom-hierarchy-generator";
import { Build, getBuilds } from "./tracking-data/builds";
import {
  CADCurrencyConverter,
  getCADCurrencyConverter,
  getCurrencies,
  getExchangeRates,
} from "./tracking-data/currency";
import { ExistingInventoryForItem, getExistingInventoryBreakdown } from "./tracking-data/existing-inventory";
import { getItemReceipts, ItemReceipt } from "./tracking-data/item-receipts";
import { getItemsNotOnSalesOrder, ItemNotOnSalesOrder } from "./tracking-data/items-not-on-sales-order";
import { getPurchaseOrders, PurchaseOrder } from "./tracking-data/purchase-orders";
import { getPurchasingPrioritiesForJob, PurchasingPriorities } from "./tracking-data/purchasing-priorities";
import { getSalesOrderBomHierarchy } from "./tracking-data/sales-order-bom-hierarchy";
import { Bin, getSalesOrderRackBin } from "./tracking-data/sales-order-rack";
import { getSalesOrder, SalesOrder } from "./tracking-data/sales-orders";
import { getShipments, Shipment } from "./tracking-data/shipments";
import { getTransfers, Transfer } from "./tracking-data/transfers";
import { getWorkOrders, WorkOrder } from "./tracking-data/work-orders";
import { BomRecordsForParents, fetchBomRecordsForParents } from "./tracking-data/bom-records";

export interface JobTrackingData {
  job: Job;
  salesOrderRackBin: Bin;
  exchangeRates: Map<string, number>;
  purchasingPriorities: PurchasingPriorities;
  currencies: Map<number, Currency>;
  CADCurrencyConverter: CADCurrencyConverter;
  salesOrder: SalesOrder;
  salesOrderBomRecords: BomRecordsForParents;
  transfers: Transfer[];
  adjustments: Adjustment[];
  itemReceipts: ItemReceipt[];
  builds: Build[];
  shipments: Shipment[];
  existingInventory: ExistingInventoryForItem[];
  existingInventoryByItemId: Map<number, ExistingInventoryForItem>;
  purchaseOrders: PurchaseOrder[];
  workOrders: WorkOrder[];
  itemsNotOnSalesOrder: ItemNotOnSalesOrder[];
  itemsNotOnSalesOrderBomRecords: BomRecordsForParents;
  salesOrderBomHierarchy: BomNode[];
  salesOrderBomComponents: BomComponent[];
  bomComponents: BomComponent[];
  bomComponentsByItemId: Map<number, BomComponent>;
}

export async function getJobTrackingData(job: Job): Promise<JobTrackingData> {
  const graph = getJobTrackingGraph(job);
  return { job, ...(await graph.runAll()) };
}

export async function updateJobTrackingData(
  { job, ...old }: JobTrackingData,
  invalidatedStepNames: (keyof Omit<JobTrackingData, "job">)[],
  precomputedResults?: Readonly<Partial<JobTrackingData>>,
): Promise<JobTrackingData> {
  const graph = getJobTrackingGraph(job);
  return { job, ...(await graph.runIncremental(old, invalidatedStepNames, precomputedResults)) };
}

function getJobTrackingGraph(job: Job) {
  const graph = new AsyncStepGraph<Omit<JobTrackingData, "job">>();

  graph.define("salesOrderRackBin", [], () => getSalesOrderRackBin(job));
  graph.define("exchangeRates", [], () => getExchangeRates());
  graph.define("purchasingPriorities", [], () => getPurchasingPrioritiesForJob(job.id));
  graph.define("currencies", ["exchangeRates"], ({ exchangeRates }) => getCurrencies(exchangeRates));
  graph.define("CADCurrencyConverter", ["currencies"], ({ currencies }) => getCADCurrencyConverter(currencies));
  graph.define("salesOrder", ["CADCurrencyConverter"], ({ CADCurrencyConverter }) =>
    getSalesOrder(job, CADCurrencyConverter),
  );
  graph.define("salesOrderBomRecords", ["salesOrder", "CADCurrencyConverter"], ({ salesOrder, CADCurrencyConverter }) =>
    fetchBomRecordsForParents(
      salesOrder.jobLines.map(({ item, baseQuantity: quantity }) => ({ item, quantity })),
      CADCurrencyConverter,
    ),
  );
  graph.define(
    "transfers",
    ["salesOrderRackBin", "CADCurrencyConverter"],
    ({ salesOrderRackBin, CADCurrencyConverter }) => getTransfers(salesOrderRackBin.id, CADCurrencyConverter),
  );
  graph.define(
    "adjustments",
    ["salesOrderRackBin", "CADCurrencyConverter"],
    ({ salesOrderRackBin, CADCurrencyConverter }) => getAdjustments(salesOrderRackBin.id, CADCurrencyConverter),
  );
  graph.define("itemReceipts", ["CADCurrencyConverter", "exchangeRates"], ({ CADCurrencyConverter, exchangeRates }) =>
    getItemReceipts(job.id, CADCurrencyConverter, exchangeRates),
  );
  graph.define("builds", ["CADCurrencyConverter"], ({ CADCurrencyConverter }) => getBuilds(job, CADCurrencyConverter));
  graph.define(
    "shipments",
    ["salesOrderRackBin", "CADCurrencyConverter"],
    ({ salesOrderRackBin, CADCurrencyConverter }) => getShipments(job.id, salesOrderRackBin.id, CADCurrencyConverter),
  );
  graph.define(
    "existingInventory",
    ["salesOrderRackBin", "transfers", "adjustments", "builds", "shipments", "itemReceipts"],
    ({ salesOrderRackBin, transfers, adjustments, builds, shipments, itemReceipts }) =>
      getExistingInventoryBreakdown(job.id, salesOrderRackBin, transfers, adjustments, builds, shipments, itemReceipts),
  );
  graph.define(
    "existingInventoryByItemId",
    ["existingInventory"],
    ({ existingInventory }) => new Map(existingInventory.map((x) => [x.item.id, x])),
  );
  graph.define("purchaseOrders", ["itemReceipts", "CADCurrencyConverter"], ({ itemReceipts, CADCurrencyConverter }) =>
    getPurchaseOrders(job.id, itemReceipts, CADCurrencyConverter),
  );
  graph.define("workOrders", ["CADCurrencyConverter"], ({ CADCurrencyConverter }) =>
    getWorkOrders(job.id, CADCurrencyConverter),
  );
  graph.define(
    "itemsNotOnSalesOrder",
    [
      "salesOrderBomRecords",
      "purchaseOrders",
      "itemReceipts",
      "workOrders",
      "builds",
      "shipments",
      "transfers",
      "adjustments",
    ],
    (deps) =>
      getItemsNotOnSalesOrder(
        deps.salesOrderBomRecords,
        deps.purchaseOrders,
        deps.itemReceipts,
        deps.workOrders,
        deps.builds,
        deps.shipments,
        deps.transfers,
        deps.adjustments,
      ),
  );
  graph.define(
    "itemsNotOnSalesOrderBomRecords",
    ["itemsNotOnSalesOrder", "CADCurrencyConverter"],
    async ({ itemsNotOnSalesOrder, CADCurrencyConverter }) =>
      fetchBomRecordsForParents(
        itemsNotOnSalesOrder.map(({ item }) => ({ item, quantity: 0 })),
        CADCurrencyConverter,
      ),
  );
  graph.define(
    "salesOrderBomHierarchy",
    ["salesOrderBomRecords", "purchasingPriorities"],
    ({ salesOrderBomRecords, purchasingPriorities }) =>
      getSalesOrderBomHierarchy(salesOrderBomRecords, purchasingPriorities),
  );

  graph.define(
    "salesOrderBomComponents",
    ["salesOrderBomHierarchy", "existingInventory"],
    ({ salesOrderBomHierarchy, existingInventory }) =>
      getSalesOrderBomComponents(salesOrderBomHierarchy, existingInventory),
  );
  graph.define(
    "bomComponents",
    ["salesOrderBomComponents", "itemsNotOnSalesOrderBomRecords", "purchasingPriorities"],
    (deps) =>
      getBomByComponent(deps.salesOrderBomComponents, deps.itemsNotOnSalesOrderBomRecords, deps.purchasingPriorities),
  );
  graph.define(
    "bomComponentsByItemId",
    ["bomComponents"],
    ({ bomComponents }) => new Map(bomComponents.map((x) => [x.item.id, x])),
  );
  return graph;
}
